import { useMemo } from "react";
import {
  alpha,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { LoadingButton } from "~/components/loading-button";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import { AggregateScoresEdges } from "~/components/FirewatchPage";
import {
  Close as CloseIcon,
  SearchOff as SearchOffIcon,
} from "@mui/icons-material";
import { ExposedAsset } from "~/pages/space/vulnerabilities/components/exposed-assets/types";
import { SECURITY_PIPELINE_DISPLAY_NAME } from "../../constants";
import WorkflowScopeTable, {
  WorkflowScopeData,
} from "~/pages/integrations/workflow/components/WorkflowScopeTable/WorkflowScopeTable";
import { GetSecurityPipelineFindingsGroupsDocument } from "~/operations";
import { useQuery } from "@apollo/client";
import { useScope } from "~/hooks/useScope";
import { Initiative } from "~/operations";

interface CreatePullRequestStepOneProps {
  cveOrAdvisoryFindings?: Array<
    CVEFindingsNode | AdvisoryFindingsNode | ExposedAsset
  >;
  aggregateScoresEdges?: AggregateScoresEdges;
  isAggregateScoresContext: boolean;
  isLoading: boolean;
  onCreatePullRequestButtonClick: () => void;
  onClose: () => void;
  initiatives?: Array<Initiative>;
}

export const CreatePullRequestStepOne = ({
  initiatives,
  isAggregateScoresContext,
  aggregateScoresEdges,
  isLoading,
  onCreatePullRequestButtonClick,
  cveOrAdvisoryFindings,
  onClose,
}: CreatePullRequestStepOneProps) => {
  const { spaceScope } = useScope();

  const isAggScores =
    isAggregateScoresContext && Boolean(aggregateScoresEdges?.length);
  const isInitiatives = Boolean(initiatives?.length) && !isAggScores;

  const initiativeMrns = useMemo(() => {
    return isInitiatives ? initiatives?.map((i) => i.mrn) || [] : [];
  }, [isInitiatives, initiatives]);

  const findingMrns = useMemo(() => {
    return isAggScores
      ? aggregateScoresEdges?.map((e) => e.node?.findingMrn) || []
      : cveOrAdvisoryFindings?.map((f) => f.mrn) || [];
  }, [isAggScores, aggregateScoresEdges, cveOrAdvisoryFindings]);

  const { data, loading: scopeDataLoading } = useQuery(
    GetSecurityPipelineFindingsGroupsDocument,
    {
      variables: {
        ownerMrn: spaceScope?.mrn || "",
        findingMrns: findingMrns,
        initiativeMrns,
        assetMrns: [],
      },
      skip: !spaceScope || (!findingMrns.length && !initiativeMrns.length),
    },
  );

  // Transform the query response to match WorkflowScopeData expected by WorkflowScopeTable
  const scopeData: WorkflowScopeData = useMemo(
    () => ({
      space: spaceScope,
      listItems: data?.securityPipelineFindingGroups?.findingGroups,
    }),
    [spaceScope, data?.securityPipelineFindingGroups?.findingGroups],
  );

  const isScopeDataEmpty =
    !scopeDataLoading &&
    !isLoading &&
    (!scopeData.listItems || scopeData.listItems.length === 0);

  return (
    <>
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 24,
          right: 24,
          color: "text.primary",
        }}
      >
        <CloseIcon />
      </IconButton>

      {!isScopeDataEmpty && (
        <DialogTitle
          variant="h5"
          sx={{
            alignSelf: "stretch",
            textAlign: "center",
            color: "text.light",
            fontWeight: 700,
            textTransform: "uppercase",
            p: 0,
            m: 0,
          }}
        >
          Fix Summary
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          p: 0,
          m: 0,
          pb: 3,
          overflow: "visible",
          mb: 2,
          overflowY: "scroll",
        }}
      >
        {!isScopeDataEmpty && (
          <Typography
            variant="body2"
            color="text.main"
            sx={{ mb: 3, textAlign: "center" }}
          >
            {SECURITY_PIPELINE_DISPLAY_NAME} will pull the necessary assets,
            apply the patch, and verify the fix. You'll be able to review the PR
            in GitHub at any time.
          </Typography>
        )}
        {isScopeDataEmpty ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 3,
              borderRadius: 2,
              backgroundColor: "background.paper",
              height: 240,
              minHeight: 240,
            }}
          >
            <SearchOffIcon
              sx={{
                fontSize: 48,
                color: (theme) => alpha(theme.palette.text.primary, 0.2),
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: (theme) => alpha(theme.palette.text.primary, 0.5),
                mb: 1,
              }}
            >
              No findings available
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: (theme) => alpha(theme.palette.text.primary, 0.4),
                textAlign: "center",
              }}
            >
              Fix currently not available for your selection.
            </Typography>
          </Box>
        ) : (
          <WorkflowScopeTable
            loading={isLoading || scopeDataLoading}
            scopeData={scopeData}
            onFindingClick={onClose}
          />
        )}
      </DialogContent>
      {!isScopeDataEmpty && (
        <DialogActions sx={{ justifyContent: "right", p: 0, m: 0 }}>
          <LoadingButton
            variant="contained"
            color="primary"
            type="submit"
            buttonText="GENERATE"
            onClick={onCreatePullRequestButtonClick}
            loading={isLoading}
          />
        </DialogActions>
      )}
    </>
  );
};
